import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { TaxApi } from "../../api/TaxApi";
import type { CatalogFetchOptions } from "../../generated/dto/CatalogFetchOptions";
import type { CatalogItemFetchOptions } from "../../generated/dto/CatalogItemFetchOptions";
import type { CatalogItemStaff } from "../../generated/dto/CatalogItemStaff";
import type { CatalogItemTaxFetchOptions } from "../../generated/dto/CatalogItemTaxFetchOptions";
import type { CatalogItemTaxStaff } from "../../generated/dto/CatalogItemTaxStaff";
import type { CatalogStaff } from "../../generated/dto/CatalogStaff";
import type { CatalogTaxFetchOptions } from "../../generated/dto/CatalogTaxFetchOptions";
import type { CatalogTaxSearchParams } from "../../generated/dto/CatalogTaxSearchParams";
import type { CatalogTaxStaff } from "../../generated/dto/CatalogTaxStaff";
import type { CatalogTaxStaffSearchResult } from "../../generated/dto/CatalogTaxStaffSearchResult";
import type { CreateCatalogItemTax } from "../../generated/dto/CreateCatalogItemTax";
import type { CreateCatalogTax } from "../../generated/dto/CreateCatalogTax";
import type { DeleteCatalogItemTaxStaff } from "../../generated/dto/DeleteCatalogItemTaxStaff";
import type { DeleteCatalogTaxStaff } from "../../generated/dto/DeleteCatalogTaxStaff";
import type { UpdateCatalogItemTax } from "../../generated/dto/UpdateCatalogItemTax";
import type { UpdateCatalogTax } from "../../generated/dto/UpdateCatalogTax";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class TaxApiClient implements TaxApi {
  constructor(private readonly client = useClient()) {}

  async getCatalogTaxStaff(id: number, options?: CatalogTaxFetchOptions): Promise<CatalogTaxStaff> {
    const { data } = await this.client.get<unknown>(`/catalogtax/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CatalogTaxFetchOptions") });
    return deserializeCbiDto<CatalogTaxStaff>(data, "generated.dto.CatalogTaxStaff");
  }

  async getCatalogItemTaxStaff(id: number, options?: CatalogItemTaxFetchOptions): Promise<CatalogItemTaxStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemtax/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.CatalogItemTaxFetchOptions") });
    return deserializeCbiDto<CatalogItemTaxStaff>(data, "generated.dto.CatalogItemTaxStaff");
  }

  async getAllCatalogTaxStaffs(options?: CatalogTaxFetchOptions): Promise<Array<CatalogTaxStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogtax/staff/all`, { params: serializeCbiDto(options, "generated.dto.CatalogTaxFetchOptions") });
    return deserializeCbiDto<Array<CatalogTaxStaff>>(data, "generated.dto.CatalogTaxStaff");
  }

  async getAllCatalogItemTaxStaffs(options?: CatalogItemTaxFetchOptions): Promise<Array<CatalogItemTaxStaff>> {
    const { data } = await this.client.get<unknown>(`/catalogitemtax/staff/all`, { params: serializeCbiDto(options, "generated.dto.CatalogItemTaxFetchOptions") });
    return deserializeCbiDto<Array<CatalogItemTaxStaff>>(data, "generated.dto.CatalogItemTaxStaff");
  }

  async createCatalogTaxStaff(source: CreateCatalogTax): Promise<CatalogTaxStaff> {
    const { data } = await this.client.post<unknown>(`/catalogtax/staff`, serializeCbiDto(source, "generated.dto.CreateCatalogTax"));
    return deserializeCbiDto<CatalogTaxStaff>(data, "generated.dto.CatalogTaxStaff");
  }

  async createCatalogItemTaxStaff(source: CreateCatalogItemTax): Promise<CatalogItemTaxStaff> {
    const { data } = await this.client.post<unknown>(`/catalogitemtax/staff`, serializeCbiDto(source, "generated.dto.CreateCatalogItemTax"));
    return deserializeCbiDto<CatalogItemTaxStaff>(data, "generated.dto.CatalogItemTaxStaff");
  }

  async updateCatalogTaxStaff(source: UpdateCatalogTax): Promise<CatalogTaxStaff> {
    const { data } = await this.client.put<unknown>(`/catalogtax/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateCatalogTax"));
    return deserializeCbiDto<CatalogTaxStaff>(data, "generated.dto.CatalogTaxStaff");
  }

  async updateCatalogItemTaxStaff(source: UpdateCatalogItemTax): Promise<CatalogItemTaxStaff> {
    const { data } = await this.client.put<unknown>(`/catalogitemtax/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateCatalogItemTax"));
    return deserializeCbiDto<CatalogItemTaxStaff>(data, "generated.dto.CatalogItemTaxStaff");
  }

  async deleteCatalogTaxStaff(source: DeleteCatalogTaxStaff): Promise<void> {
    await this.client.delete(`/catalogtax/staff/${source.assignId}`);
  }

  async deleteCatalogItemTaxStaff(source: DeleteCatalogItemTaxStaff): Promise<void> {
    await this.client.delete(`/catalogitemtax/staff/${source.assignId}`);
  }

  async searchCatalogTaxStaff(params: CatalogTaxSearchParams, page: number, perPage: number): Promise<PaginationResult<CatalogTaxStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/catalogtax/staff/search`, serializeCbiDto(params, "generated.dto.CatalogTaxSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<CatalogTaxStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<CatalogTaxStaffSearchResult>>(result.items, "generated.dto.CatalogTaxStaffSearchResult") };
    return result;
  }

  async searchCatalogTaxStaffFull(params: CatalogTaxSearchParams, page: number, perPage: number, options?: CatalogTaxFetchOptions): Promise<PaginationResult<CatalogTaxStaff>> {
    const { data } = await this.client.post<unknown>(`/catalogtax/staff/search/full`, serializeCbiDto(params, "generated.dto.CatalogTaxSearchParams"), { params: { ...(serializeCbiDto(options, "generated.dto.CatalogTaxFetchOptions") as Record<string, unknown>), ...{ page, perPage } } });
    let result = data as PaginationResult<CatalogTaxStaff>;
    result = { ...result, items: deserializeCbiDto<Array<CatalogTaxStaff>>(result.items, "generated.dto.CatalogTaxStaff") };
    return result;
  }

  async getCatalogTaxStaffCatalogId(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff> {
    const { data } = await this.client.get<unknown>(`/catalogtax/staff/${id}/catalogid`, { params: serializeCbiDto(options, "generated.dto.CatalogFetchOptions") });
    return deserializeCbiDto<CatalogStaff>(data, "generated.dto.CatalogStaff");
  }

  async getCatalogItemTaxStaffItemId(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemtax/staff/${id}/itemid`, { params: serializeCbiDto(options, "generated.dto.CatalogItemFetchOptions") });
    return deserializeCbiDto<CatalogItemStaff>(data, "generated.dto.CatalogItemStaff");
  }

  async getCatalogItemTaxStaffTaxId(id: number, options?: CatalogTaxFetchOptions): Promise<CatalogTaxStaff> {
    const { data } = await this.client.get<unknown>(`/catalogitemtax/staff/${id}/taxid`, { params: serializeCbiDto(options, "generated.dto.CatalogTaxFetchOptions") });
    return deserializeCbiDto<CatalogTaxStaff>(data, "generated.dto.CatalogTaxStaff");
  }
}

export const useTaxApi = (): TaxApi => new TaxApiClient();
